import { useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useModuleDefinition } from '@platform/modularity/useModuleDefinition';
import { ModuleStringsProvider } from '@platform/modularity/ModuleStringsProvider';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { ErrorBanner, Spinner } from '@platform/ui';
import { routes } from '@platform/core/lib/routes';
import { useModuleCrumbs } from '@platform/engine/list/header/useModuleCrumbs';
import { childModuleType } from '@platform/engine/menu/childModuleType';
import { mainRecordTypeOf, settingsItems } from '@platform/engine/menu/moduleSections';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import type { ISettingsLinks } from './settings/ISettingsLinks';
import { recordsFocusOf } from './settings/recordsFocus';
import { SettingsFieldsPage } from './settings/SettingsFieldsPage';
import { SettingsFormPage } from './settings/SettingsFormPage';
import { SettingsRecordsPage } from './settings/SettingsRecordsPage';
import './settings/settings.css';

/** Параметр адреси з типом, підсвіченим у «Записах й формах» (пункти ТАБЛИЦІ й довідників лівого меню, прототип v4 cmFocus). */
const FOCUS_PARAM = 'focus';
/** Параметр адреси з дочірньою таблицею, налаштування якої відкрито (окремий модуль прототипу v4: «Видатки»; без нього — основний тип). */
const TYPE_PARAM = 'type';
/** Пункти «Налаштувань», що відкриваються для дочірньої таблиці (пункти меню модуля; «Статуси» й «Типи» — довідники основного типу). */
const CHILD_ITEMS = ['fields', 'form', 'records'];

/** Підпис останнього рівня крихт для пункту (прототип v4: «Налаштування», «Форма», «Записи й форми» — і для «Статусів», «Типів»). */
const CRUMB: Record<string, string> = { fields: 'pageHead.settings', form: 'settings.form', records: 'settings.records', statuses: 'settings.records', types: 'settings.records' };

/**
 * /:group/:module/settings/:item — група «Налаштування» модуля за прототипом v4 (inMod — відкрито з меню модуля): «Поля й подання» —
 * конструктор типу (прототип page==='builder' з bScoped: той самий редактор, що й «Налаштування › Конструктор модулів», без списку
 * модулів; кожна зміна одразу зберігається перевизначенням простору, ADR-023); «Форма» — редактор форми з переглядом; «Записи й форми»,
 * «Статуси», «Типи» — типи записів і форми модуля (підсвічено тип з `?focus=` — спроєктовані таблиці й довідники з лівого меню, — інакше
 * довідник статусів чи типів). Повернення «← Модуль» і крихта модуля повертають до модуля без скидання (прототип page:'module').
 * Дочірня таблиця з `?type=` (чи `?focus=`) — окремий модуль прототипу: «Поля й подання», «Форма» й «Записи й форми» відкриваються для
 * неї, крихти й повернення називають її — «Простір › Офіс › Видатки › Форма», «← Видатки» — і ведуть до її списку. З «Налаштування» ті
 * самі сторінки відкриває CustomizeModulePage.
 */
export function ModuleSettingsPage() {
  const { ws, module: moduleKey, item } = useWorkspaceParams();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const { data: module, isLoading, error } = useModuleDefinition(moduleKey);
  const strings = useModuleStrings(moduleKey);
  const moduleName = module ? strings.moduleName(module.module.name) : '';
  const child = module && CHILD_ITEMS.includes(item ?? '') ? childModuleType(module, params.get(TYPE_PARAM) ?? params.get(FOCUS_PARAM)) : undefined;
  const ownerName = child ? strings.entity(child.key, 'name', child.name) : moduleName;
  const ownerTo = child ? routes.list(moduleKey, child.key) : routes.moduleReturn(moduleKey);
  const crumb = t(CRUMB[item ?? ''] ?? 'pageHead.settings');
  const tail = useMemo(() => [{ label: crumb }], [crumb]);
  const crumbs = useModuleCrumbs(moduleKey, ownerName, tail, ownerTo);
  const links = useMemo<ISettingsLinks>(
    () => ({ backLabel: t('modSettings.back', { name: ownerName }), backTo: ownerTo, formTo: routes.moduleSettings(moduleKey, 'form', undefined, child?.key) }),
    [t, ownerName, ownerTo, moduleKey, child],
  );
  if (isLoading) return <Spinner />;
  if (error || !module) return <ErrorBanner error={error ?? { message: t('app.notFound') }} />;
  if (!item || !settingsItems.includes(item)) return <Navigate to={routes.moduleSettings(moduleKey, settingsItems[0])} replace />;
  const rt = mainRecordTypeOf(module);
  if (!rt) return <ErrorBanner error={{ message: t('app.notFound') }} />;
  const page = child ?? rt;
  if (item === 'fields') {
    return <ModuleStringsProvider strings={strings}><SettingsFieldsPage key={page.key} ws={ws} module={module} rt={page} name={ownerName} crumbs={crumbs} /></ModuleStringsProvider>;
  }
  if (item === 'form') {
    return <ModuleStringsProvider strings={strings}><SettingsFormPage key={page.key} ws={ws} module={module} rt={page} name={moduleName} crumbs={crumbs} links={links} /></ModuleStringsProvider>;
  }
  return (
    <ModuleStringsProvider strings={strings}>
      <SettingsRecordsPage ws={ws} module={module} rt={child} focus={child ? undefined : params.get(FOCUS_PARAM) || recordsFocusOf(module, item)} crumbs={crumbs} links={links} />
    </ModuleStringsProvider>
  );
}
